/**
 * Pure formatter for console messages captured by PlaywrightClient.
 *
 * Extracted so the filter/format rules (step-scoping, pre-step tagging,
 * `+Δms` offsets) can be unit-tested without spinning up a browser.
 *
 * @module playwright-client/console-messages-formatter
 */

import type { CapturedConsoleMessage } from './internal-types.js';

export interface FormatConsoleMessagesOpts {
  onlyErrors?: boolean;
  /** Wall-clock ms cutoff — by default only messages with `timestampMs >= sinceMs` are included. */
  sinceMs?: number;
  /** If true, include messages captured before `sinceMs` (tagged as pre-step). */
  includePreStep?: boolean;
}

/**
 * Filter + render the captured console buffer per the given options.
 * Returns the multi-line string body (without the MCP wrapper) so callers
 * can wrap it however they need. Returns `null` when there's nothing to render,
 * and the caller is expected to supply the appropriate empty-state message.
 */
export function formatConsoleMessages(
  captured: readonly CapturedConsoleMessage[],
  opts?: FormatConsoleMessagesOpts
): { lines: string[]; preStepHidden: number } {
  let messages = opts?.onlyErrors ? captured.filter((m) => m.type === 'error') : captured.slice();

  const sinceMs = opts?.sinceMs;
  const includePreStep = opts?.includePreStep === true;
  let preStepHidden = 0;
  if (sinceMs !== undefined && !includePreStep) {
    const before = messages.length;
    messages = messages.filter((m) => m.timestampMs >= sinceMs);
    preStepHidden = before - messages.length;
  }

  const lines = messages.map((m) => {
    const isPreStep = sinceMs !== undefined && m.timestampMs < sinceMs;
    const tag =
      sinceMs !== undefined ? ` ${isPreStep ? '' : '+'}${m.timestampMs - sinceMs}ms` : '';
    const suffix = isPreStep ? ' (before this step started — may not be relevant)' : '';
    return `[${m.type.toUpperCase()}${tag}] ${m.text} @ ${m.location}${suffix}`;
  });

  return { lines, preStepHidden };
}

/** Empty-state message that matches the semantics expected by the MCP tool agent. */
export function emptyConsoleMessagesText(opts?: FormatConsoleMessagesOpts): string {
  return opts?.sinceMs !== undefined
    ? 'No console messages captured during this step (set includePreStep: true to see earlier messages).'
    : 'No console messages captured';
}
